import { ReactNode } from 'react';
import { PriceRange, SearchRequest } from '@mango-engineering/maknet-client-sdk/lib/shop/search/types';
import { AppMode, ProductCardVariant, RecommendedType } from '../utils/constants';
import { Product } from './product.types';

export type LocaleType = 'en' | 'th';
export type PlatformType = 'web' | 'app';

export interface FilterArray {
  attribute: string;
  values: string[];
}
export interface TitleData {
  text: string;
  icon?: boolean;
}

export type SearchFilter = {
  priceRange?: PriceRange<number>;
  collections?: string[];
  availability?: boolean;
  brands?: string[];
  hasPromotions?: boolean;
  storeCode?: string;
  storeCodes?: string[];
  category?: string[];
  isSalesCustomer?: boolean;
  isMakroPro?: boolean;
  delivery24Hr?: boolean;
  searchFilter?: string;
  isInStore?: boolean;
  collectionIds?: string[];
  channels?: string[];
};

export interface BaseCarouselConfig {
  titleKey: string;
  productCardVarient?: ProductCardVariant;
  title?: string;
  titleIcon?: boolean;
  seeAllRouterPath?: string;
  data?: Product[] | RecommendationResponse;
  apikey?: string;
  queryParam?: string;
  productId?: string;
}

export interface ProductBasedConfig extends BaseCarouselConfig {
  recommendedType: RecommendedType.PRODUCT_BASED;
}

export interface UserBasedConfig extends BaseCarouselConfig {
  recommendedType: RecommendedType.USER_BASED;
  userId?: string;
}

export type CarouselConfigProps = ProductBasedConfig | UserBasedConfig;

export type CarouselMaxItemsCount = {
  web: number;
  mobile: number;
};

export type CarouselSettings = CarouselConfigProps & {
  type: string;
  locale: LocaleType | null;
  platform: PlatformType | null;
  data: Product[];
  carouselMaxItemsCount?: CarouselMaxItemsCount;
};

export type CarouselSettingProps = {
  recommendationType?: string;
  titleKey: string;
  productCardVariant?: ProductCardVariant;
  title?: string;
  titleIcon?: boolean;
  seeAllRouterPath?: string;
  data?: RecommendationResponse;
  apikey?: string;
  queryParam?: string;
  productId?: string;
  query?: string;
  carouselIndex?: number;
  platform?: PlatformType;
  carouselMaxItemsCount?: CarouselMaxItemsCount;
  isDiversify?: boolean;
  fetchMoreData?: () => void;
  shouldFetch?: boolean;
  isVerticalScroll?: boolean;
  isHorizonProductCardEnabled?: boolean;
  isPaginationLoading?: boolean;
};

export type RecommendationBannerTargetType = 'FLEXI_PAGE' | 'COLLECTION';

export type RecommendationBanner = {
  targetType: RecommendationBannerTargetType;
  bannerUrl: string;
  title?: string;
  name?: string;
  pageHandle?: string;
  collectionId?: string | number;
};

export interface ProductListProps {
  slides: ReactNode[];
  title?: string;
  header?: {
    title?: ReactNode;
    children?: ReactNode;
  };
  isHorizonLayout?: boolean;
  hasMore?: boolean;
  fetchMoreData?: () => void;
  shouldFetch?: boolean;
  showHeader?: boolean;
  onCarouselScroll?: () => void;
  onHeightChange?: () => void;
  isLoading?: boolean;
  resetScrollKey?: string;
}

export const SEARCH_PERFORM_TRIGGER_TYPE = {
  SEE_ALL: 'seeAll',
  ENTER: 'enter',
  DIRECT_CLICK: 'directClick',
  HISTORY_CLICK: 'historyClick',
  PLUS_BUTTON: 'plusButton',
  QUANTITY_COUNT: 'quantityCount',
  PRODUCT_CARD: 'productCard',
  SEE_ALL_CARD: 'seeAllCard',
};

export enum RecommendationType {
  BRANDS = 'brand',
  CATEGORIES = 'category',
  BOUGHTTOGETHER = 'boughtTogether',
  HOME = 'home',
  TOPPICKS = 'topPicks',
  BUY_AGAIN = 'buyAgain',
  SEARCH_START_TOP_PICKS = 'searchStartTopPicks',
  ADSCAROUSEL = 'adsCarousel',
  BASKET_RECOMMENDATION = 'basketRecommendation',
  FRESHFOOD = 'freshRecommendations',
  RECOMMENDED_FOR_YOU = 'recommended_for_you',
  HOME_RECOMMENDATIONS = 'homeRecommendation',
  AGEING_RECOMMENDATIONS = 'ageingRecommendation',
  NEW_ARRIVALS_RECOMMENDATIONS = 'newlyOnboardedRecommendations',
  PERSONALIZED = 'PERSONALIZED',
  CUSTOM = 'CUSTOM',
}
export type RecommendationPayload = {
  page?: number;
  locale?: string;
  type?: RecommendationType;
  productId?: string;
  isExpressMode?: boolean;
  expressDelivery?: boolean;
  storeCodes?: string[];
  recommendationStoreCodes?: string[];
  expressStoreCodes?: string[];
  afcStoreCodes?: string[];
  isAfcStoreEnabled?: boolean;
  hasAlcoholProductAvailability?: boolean;
  accessToken?: string;
  userId?: string;
  userSegments?: string[];
  isHorecaMemberEnabled?: boolean;
  diversifyResults?: boolean;
  recommendationVariant?: string;
  isSameDay?: boolean;
  isPromotionPLP?: boolean;
  searchRequest?: SearchRequest;
  includeCurations?: boolean;
  appMode?: AppMode;
  countryCode?: string;
};

export type RecommendationResponse = {
  products: Product[];
  recommendationType: string;
  totalCount: number;
  actualCount?: number;
  algorithm?: string;
  personalised?: boolean;
};

export type BrandAndCatrgoryRecommendationResult = {
  recommendations: RecommendationProductIds[];
  total: number;
  personalised?: boolean;
};
export type RecommendationProductIds = {
  productId: string;
  storeCodes: string[];
  similarity: number;
  vector_distance?: number;
};

export interface RecommendationPieceRequest {
  productId: string;
  storeCode?: string;
  size: number;
  storeCodes?: string[];
  page?: number;
  type?: RecommendationType;
  expressDelivery?: boolean;
}
export interface HomeRecommendationRequest {
  page: number;
  size: number;
  diversifyResults?: boolean;
  countryCode?: string;
}

export type HomeRecommendationResult = {
  products: HomeRecommendationProducts[];
  recommendationType: string;
  totalCount: number;
  actualCount?: number;
  algorithm?: string;
  personalised?: boolean;
  cacheTTL?: number;
};

export type HomeRecommendationProducts = {
  id: string;
  position: number;
  recommendationScore: number;
  alreadyPurchased: boolean;
  normalisedRecommendationScore?: number;
};

export type InjectedSearchData = {
  storeCodes: string[];
  accessToken: string;
}
